/**
 * Auth utilities for token management and session state
 */

/**
 * Check if a JWT token is expired or will expire soon
 * @param token The JWT token to check
 * @param bufferSeconds Number of seconds before actual expiration to consider a token as expired (default: 30)
 * @returns Boolean indicating if the token is expired or will expire soon
 */
export const isTokenExpiredOrExpiringSoon = (token: string | null, bufferSeconds = 30): boolean => {
  if (!token) return true;
  
  try {
    // Extract payload from JWT token
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    
    // Get expiration time in milliseconds
    const expiryTime = payload.exp * 1000;
    
    // Consider token as expired if it will expire within the buffer time
    return Date.now() >= (expiryTime - bufferSeconds * 1000);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If we can't determine, assume it's expired
  }
};

/**
 * Parses JWT token error message to determine if it's an expiration issue
 * @param error Error object or message
 * @returns Boolean indicating if the error is related to token expiration
 */
export const isTokenExpirationError = (error: Error | string | unknown): boolean => {
  const errorMessage = typeof error === 'string' 
    ? error 
    : (error instanceof Error ? error.message : String(error));
  
  return errorMessage.toLowerCase().includes('expired') || 
    errorMessage.toLowerCase().includes('jwt') ||
    errorMessage.toLowerCase().includes('token');
};

/**
 * Stores information about the current page before redirecting for authentication
 * @param path Current path to return to after login
 * @param reason Reason for the redirect (e.g., "Session expired")
 */
export const storeRedirectInfo = (path: string, reason: string): void => {
  localStorage.setItem('auth_redirect_path', path);
  localStorage.setItem('auth_redirect_reason', reason);
  localStorage.setItem('auth_redirect_timestamp', Date.now().toString());
};

/**
 * Retrieves stored redirect information
 * @returns Object containing redirect path, reason and timestamp
 */
export const getRedirectInfo = (): { path: string | null; reason: string | null; timestamp: number } => {
  const path = localStorage.getItem('auth_redirect_path');
  const reason = localStorage.getItem('auth_redirect_reason');
  const timestamp = parseInt(localStorage.getItem('auth_redirect_timestamp') || '0', 10);
  
  return { path, reason, timestamp };
};

/**
 * Clears the redirect reason (and timestamp) but keeps the path, so the post-login
 * destination remains available through the Google OAuth round trip.
 */
export const clearRedirectReason = (): void => {
  localStorage.removeItem('auth_redirect_reason');
  localStorage.removeItem('auth_redirect_timestamp');
};

/**
 * Clears stored redirect information (path, reason, timestamp)
 */
export const clearRedirectInfo = (): void => {
  localStorage.removeItem('auth_redirect_path');
  localStorage.removeItem('auth_redirect_reason');
  localStorage.removeItem('auth_redirect_timestamp');
}; 